// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * API Save/Load Manager
 * Wraps SaveLoadManager to sync with backend API.
 * Tracks the last known server save ID to enable conflict detection.
 */

import saveLoadManager from './SaveLoadManager';
import { api } from '../../api/client';

class ApiSaveLoadManager {
  constructor() {
    this.lastSaveId = null;
  }

  /**
   * Save current game state to API.
   * Returns { success: true }, { success: false }, or { conflict: true } on 409.
   */
  async saveToApi(token, saveName = 'autosave') {
    try {
      const gameState = saveLoadManager.getCurrentState();
      await api.game.saveGame(token, saveName, gameState, this.lastSaveId);
      return { success: true };
    } catch (error) {
      if (error.status === 409) {
        console.warn('[ApiSaveLoad] Save conflict — server has a newer save than our last load.');
        return { conflict: true, serverSaveId: error.data?.server_save_id };
      }
      console.error('[ApiSaveLoad] Save failed:', error);
      return { success: false };
    }
  }

  /**
   * Load latest game state from API.
   * Caches the returned save ID for future conflict detection.
   *
   * Only triggers SaveLoadManager's reload-on-load when the server save
   * actually differs from what's already in the local save slot (compared
   * by the save's own `timestamp` field). Without this check, since this
   * runs on every mount while authenticated (see App.js), an unconditional
   * reload here would reload → re-mount → re-authenticate → reload again,
   * forever. See SaveLoadManager.load()'s docstring for why reload can't
   * just be skipped either — the two constraints together are why this
   * compare-then-maybe-reload shape exists instead of either extreme.
   */
  async loadFromApi(token) {
    try {
      const response = await api.game.loadGame(token);
      if (response && response.game_state) {
        // Track which save we loaded so we can detect conflicts on next save
        this.lastSaveId = response.save_id || null;

        const slotKey = saveLoadManager.getSaveKey(1);
        const existingRaw = localStorage.getItem(slotKey);
        const existingTimestamp = existingRaw ? JSON.parse(existingRaw)?.timestamp : null;
        const incomingTimestamp = response.game_state.timestamp;

        if (existingTimestamp && incomingTimestamp && existingTimestamp === incomingTimestamp) {
          // Already in sync — don't write or reload, avoids disrupting
          // an in-progress session every time this runs on mount.
          return true;
        }

        localStorage.setItem(slotKey, JSON.stringify(response.game_state));
        saveLoadManager.load(1); // Reloads the page — see SaveLoadManager.load().
        return true;
      }
      return false;
    } catch (error) {
      console.error('[ApiSaveLoad] Load failed:', error);
      return false;
    }
  }

  /**
   * Auto-save to API (called periodically).
   */
  async autoSave(token) {
    if (!token) return false;
    return this.saveToApi(token, 'autosave');
  }

  /**
   * Get list of saves from API.
   */
  async listSaves(token) {
    try {
      return await api.game.listSaves(token);
    } catch (error) {
      console.error('[ApiSaveLoad] List saves failed:', error);
      return [];
    }
  }

  /**
   * Load settings from API.
   */
  async loadSettings(token) {
    try {
      return await api.game.getSettings(token);
    } catch (error) {
      console.error('[ApiSaveLoad] Load settings failed:', error);
      return null;
    }
  }

  /**
   * Save settings to API.
   */
  async saveSettings(token, settings) {
    try {
      await api.game.updateSettings(token, settings);
      return true;
    } catch (error) {
      console.error('[ApiSaveLoad] Save settings failed:', error);
      return false;
    }
  }
}

const apiSaveLoadManager = new ApiSaveLoadManager();
export default apiSaveLoadManager;
